import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Button, Card, Chip, Grid, InputLabel, MenuItem, FormControl, Select, Typography, IconButton, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Phone, PhoneForwarded, PhoneMissed, Group, Person, Visibility, PlayCircle } from '@mui/icons-material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { BookingService } from '../Booking/Service';
import { formatDateTime } from '../../utils/dateUtils';
import Loading from '../../components/Loader/Loading';
import Swal from 'sweetalert2';

const statCards = [
    { key: 'total', label: 'Total Calls', color: '#1565c0', icon: <Phone /> },
    { key: 'connected', label: 'Connected', color: '#2e7d32', icon: <PhoneForwarded /> },
    { key: 'missed', label: 'Missed', color: '#d32f2f', icon: <PhoneMissed /> },
    { key: 'customer', label: 'To Customer', color: '#c62828', icon: <Person /> },
    { key: 'provider', label: 'To Expert', color: '#1565c0', icon: <Group /> },
];

const statusColor = {
    initiated: '#fb8c00',
    dialing: '#1565c0',
    answered: '#2e7d32',
    ended: '#6a1b9a',
    failed: '#d32f2f',
    completed: '#2e7d32',
};

const MyOperatorDashboard = () => {
    const [stats, setStats] = useState(null);
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAdmin, setSelectedAdmin] = useState('all');
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [rowCount, setRowCount] = useState(0);

    const fetchData = useCallback(async (page, pageSize) => {
        setLoading(true);
        const params = {
            startDate: selectedDate.startOf('day').format('YYYY-MM-DD'),
            endDate: selectedDate.endOf('day').format('YYYY-MM-DD'),
            page: page + 1,
            pageSize,
        };
        const [statsRes, adminsRes] = await Promise.all([
            BookingService.getMyoperatorStats(params),
            BookingService.getMyoperatorAdmins(),
        ]);
        if (statsRes?.success === 1) {
            setStats(statsRes.data);
            setRowCount(statsRes.data.total || 0);
        }
        if (adminsRes?.success === 1) {
            setAdmins(adminsRes.data);
        }
        setLoading(false);
    }, [selectedDate]);

    useEffect(() => {
        fetchData(paginationModel.page, paginationModel.pageSize);
    }, [fetchData, paginationModel]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const configuredAdmins = useMemo(() => {
        return admins.filter(a => a.configured);
    }, [admins]);

    const adminList = useMemo(() => {
        return configuredAdmins.map(a => a.name).filter(Boolean).sort();
    }, [configuredAdmins]);

    const adminCounts = useMemo(() => {
        if (!stats?.calls) return {};
        const counts = {};
        stats.calls.forEach(c => {
            const name = c.initiatedByName || 'Unknown';
            counts[name] = (counts[name] || 0) + 1;
        });
        return counts;
    }, [stats]);

    const filteredCalls = useMemo(() => {
        if (!stats?.calls) return [];
        if (selectedAdmin === 'all') return stats.calls;
        return stats.calls.filter(c => c.initiatedByName === selectedAdmin);
    }, [stats, selectedAdmin]);

    const columns = [
        {
            field: 'createdAt', headerName: 'Time', sortable: false, minWidth:150, flex: 0.5,
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Time</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '.75rem' }}>{formatDateTime(params.value)}</Typography>
                </Box>
            ),
        },
        {
            field: 'target', headerName: 'Type', sortable: false, minWidth: 130, flex: 0.4,
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Type</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 0.5 }}>
                    <Chip label={params.value === 'provider' ? 'Expert' : 'Customer'}
                        sx={{ fontSize: '0.65rem', fontWeight: 600, height: 22,
                            bgcolor: params.value === 'provider' ? '#e3f2fd' : '#fce4ec',
                            color: params.value === 'provider' ? '#1565c0' : '#c62828' }}
                    />
                    {params.row.isNewUser === true && (
                        <Chip label="New" size="small"
                            sx={{ fontSize: '0.6rem', fontWeight: 700, height: 20,
                                bgcolor: '#e8f5e9',
                                color: '#2e7d32' }}
                        />
                    )}
                </Box>
            ),
        },
        {
            field: 'name', headerName: 'Name', sortable: false, minWidth: 130, flex: 1,
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Name</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '.75rem' }}>{params.value}</Typography>
                </Box>
            ),
            valueGetter: (params) => params.row.customerName || params.row.providerName || params.row.customerPhone,
        },
        {
            field: 'customerPhone', headerName: 'Phone', sortable: false, minWidth: 110, flex: 0.5,
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Phone</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '.75rem' }}>{params.value}</Typography>
                </Box>
            ),
        },
        {
            field: 'category', headerName: 'Category', sortable: false, minWidth: 90, flex: 0.4,
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Category</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '.75rem' }}>{params.value || '-'}</Typography>
                </Box>
            ),
        },
        {
            field: 'direction', headerName: 'Direction', sortable: false, minWidth: 90, flex: 0.3,
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Direction</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    {params.value ? (
                        <Chip label={params.value} size="small"
                            sx={{ fontSize: '0.6rem', fontWeight: 600, height: 20,
                                textTransform: 'capitalize',
                                bgcolor: params.value === 'incoming' ? '#e8f5e9' : '#e3f2fd',
                                color: params.value === 'incoming' ? '#2e7d32' : '#1565c0' }}
                        />
                    ) : <Typography sx={{ fontSize: '.75rem', color: '#9e9e9e' }}>-</Typography>}
                </Box>
            ),
        },
        {
            field: 'source', headerName: 'Source', sortable: false, minWidth: 105, flex: 0.3,
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Source</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    {params.value ? (
                        <Chip label={params.value === 'myoperator' ? 'MyOperator' : 'Cudel'} size="small"
                            sx={{ fontSize: '0.6rem', fontWeight: 600, height: 20,
                                bgcolor: params.value === 'myoperator' ? '#fff3e0' : '#e3f2fd',
                                color: params.value === 'myoperator' ? '#e65100' : '#1565c0' }}
                        />
                    ) : <Typography sx={{ fontSize: '.75rem', color: '#9e9e9e' }}>-</Typography>}
                </Box>
            ),
        },
        {
            field: 'duration', headerName: 'Duration', sortable: false, minWidth: 80, flex: 0.3,
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Duration</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '.75rem' }}>
                        {params.value > 0 ? `${Math.floor(params.value / 60)}m ${params.value % 60}s` : '-'}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'status', headerName: 'Status', sortable: false, minWidth: 90, flex: 0.4,
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Status</span>,
            renderCell: (params) => {
                const ps = params.row.providerStatus;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Chip label={params.value} size="small"
                            sx={{ fontSize: '0.65rem', fontWeight: 600, height: 22,
                                bgcolor: `${statusColor[params.value]}18`,
                                color: statusColor[params.value] }}
                        />
                        {ps && ps !== params.value && (
                            <Chip label={ps} size="small"
                                sx={{ fontSize: '0.55rem', fontWeight: 500, height: 16,
                                    bgcolor: ps === 'bridged' ? '#e8f5e9' : '#fff3e0',
                                    color: ps === 'bridged' ? '#2e7d32' : '#e65100' }}
                            />
                        )}
                    </Box>
                );
            },
        },
        {
            field: 'initiatedByName', headerName: 'Initiated By', sortable: false, minWidth: 130, flex: 0.6,
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Initiated By</span>,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '.75rem' }}>{params.value || '-'}</Typography>
                </Box>
            ),
        },
        {
            field: 'recording', headerName: 'Recording', sortable: false, minWidth: 80, flex: 0.3,
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Recording</span>,
            renderCell: (params) => {
                const rec = params.row.recordingFilename;
                if (!rec) return <Typography sx={{ fontSize: '.75rem', color: '#9e9e9e' }}>-</Typography>;
                const url = `https://in.app.myoperator.com/audio/${rec.replace(/\.mp3$/, '')}`;
                return (
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                        <a href={url} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: '.75rem', color: '#1565c0', textDecoration: 'underline', cursor: 'pointer' }}>
                            Download
                        </a>
                    </Box>
                );
            },
        },
        {
            field: 'events', headerName: 'Details', sortable: false, minWidth: 60, flex: 0.2,
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Details</span>,
            renderCell: (params) => {
                return (
                    <Tooltip title="View call details">
                        <IconButton size="small" sx={{ fontSize: '0.6rem', p: 0.25 }}
                            onClick={async () => {
                                const callId = params.row._id || params.row.id;
                                if (!callId) return;
                                try {
                                    const res = await BookingService.getCallDetail(callId);
                                    if (res?.success !== 1 || !res.data) {
                                        Swal.fire('Error', 'Failed to load call details', 'error');
                                        return;
                                    }
                                    const call = res.data;
                                    const events = call.webhookEvents || [];
                                    if (!events.length) {
                                        Swal.fire('No Events', 'No webhook events for this call', 'info');
                                        return;
                                    }
                                    const formatDateTime = (dt) => dt ? new Date(dt).toLocaleString() : '-';
                                    const cards = events.map((e, i) => {
                                        const d = e.data || {};
                                        const p = d?.payload || {};
                                        const legs = p?.legs || [];
                                        const agentLeg = legs.find(l => l.type === 'agent' || l.agent) || {};
                                        const custLeg = legs.find(l => l.type === 'customer' && !l.agent) || legs[1] || {};
                                        const agent = agentLeg.agent || {};
                                        return `<div style="border:1px solid #e0e0e0;border-radius:8px;margin-bottom:10px;overflow:hidden">
                                            <div style="background:${i % 2 === 0 ? '#e3f2fd' : '#f3e5f5'};padding:8px 12px;display:flex;justify-content:space-between;align-items:center">
                                                <span style="font-weight:600;font-size:13px">#${i + 1} ${d.event_type || e.event}</span>
                                                <span style="font-size:11px;color:#666">${e.receivedAt ? formatDateTime(e.receivedAt) : '-'}</span>
                                            </div>
                                            <div style="padding:10px 12px;font-size:12px;line-height:1.8">
                                                <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px 16px">
                                                    <div><b>Direction:</b> ${d.direction || '-'}</div>
                                                    <div><b>Status:</b> <span style="color:${p?.status === 'bridged' ? '#2e7d32' : '#fb8c00'};font-weight:500">${p?.status || '-'}</span></div>
                                                    ${p?.duration ? `<div><b>Duration:</b> ${Math.floor(p.duration / 60)}m ${p.duration % 60}s</div>` : ''}
                                                    ${p?.billable !== undefined ? `<div><b>Billable:</b> ${p.billable ? '✅' : '❌'}</div>` : ''}
                                                    ${p?.location ? `<div><b>Location:</b> ${p.location}</div>` : ''}
                                                    <div><b>System:</b> ${d.system_identifier || '-'}</div>
                                                    <div><b>Customer:</b> ${d.customer_identifier || '-'}</div>
                                                    ${p?.started_at ? `<div><b>Started:</b> ${new Date(p.started_at).toLocaleString()}</div>` : ''}
                                                    ${p?.ended_at ? `<div><b>Ended:</b> ${new Date(p.ended_at).toLocaleString()}</div>` : ''}
                                                    ${p?.recording_filename ? `<div style="grid-column:1/-1"><b>Recording:</b> ${p.recording_filename}</div>` : ''}
                                                </div>
                                                ${legs.length > 0 ? `<div style="margin-top:8px;border-top:1px solid #eee;padding-top:8px"><b>Legs:</b></div>
                                                <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-top:4px">
                                                    ${legs.map((leg, li) => `<div style="background:#fafafa;border-radius:6px;padding:6px 8px;border:1px solid #f0f0f0">
                                                        <div style="font-weight:500;font-size:11px;color:${leg.type === 'agent' || leg.agent ? '#1565c0' : '#c62828'};margin-bottom:2px">Leg ${li + 1}: ${leg.type || (leg.agent ? 'agent' : 'customer')}</div>
                                                        <div style="font-size:11px;color:#555">Phone: ${leg.phone_number || '-'}</div>
                                                        ${leg.dial_status ? `<div style="font-size:11px;color:#555">Status: ${leg.dial_status}</div>` : ''}
                                                        ${leg.talk_duration ? `<div style="font-size:11px;color:#555">Talk: ${leg.talk_duration}s</div>` : ''}
                                                        ${leg.ring_duration ? `<div style="font-size:11px;color:#555">Ring: ${leg.ring_duration}s</div>` : ''}
                                                        ${leg.agent?.name ? `<div style="font-size:11px;color:#555">Agent: ${leg.agent.name}${leg.agent.extension ? ` (ext: ${leg.agent.extension})` : ''}</div>` : ''}
                                                        ${leg.answered_at ? `<div style="font-size:11px;color:#555">Answered: ${new Date(leg.answered_at).toLocaleTimeString()}</div>` : ''}
                                                    </div>`).join('')}
                                                </div>` : ''}
                                                ${p?.obd_campaign?.id ? `<div style="margin-top:6px;font-size:11px;color:#888">Campaign: ${p.obd_campaign.id}</div>` : ''}
                                            </div>
                                        </div>`;
                                    }).join('');
                                    Swal.fire({
                                        title: `Call Details`,
                                        html: `<div style="max-height:70vh;overflow-y:auto">${cards}</div>`,
                                        width: 650,
                                        showConfirmButton: true,
                                        confirmButtonText: 'Close',
                                    });
                                } catch (err) {
                                    Swal.fire('Error', 'Failed to load call details', 'error');
                                }
                            }}
                        >
                            <Visibility sx={{ fontSize: '0.75rem' }} />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: 'error', headerName: 'Error', sortable: false, minWidth: 150, flex: 1,
            renderHeader: () => <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Error</span>,
            renderCell: (params) => params.row.status === 'failed' && params.row.errorDetails?.response?.details ? (
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '.75rem', color: '#d32f2f' }}>{params.row.errorDetails.response.details}</Typography>
                </Box>
            ) : <Typography sx={{ fontSize: '.75rem', color: '#9e9e9e' }}>-</Typography>,
        },
    ];

    return (
        <Box sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', mb: 2 }}>MyOperator Dashboard</Typography>

            {loading ? (
                <Loading />
            ) : stats ? (
                <>
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                        {statCards.map(({ key, label, color }) => (
                            <Grid item xs={6} md={2.4} key={key}>
                                <Card sx={{ p: 1.5, textAlign: 'center', borderLeft: `3px solid ${color}` }}>
                                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color }}>{stats[key] ?? 0}</Typography>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#757575' }}>{label}</Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Card sx={{ p: 2, mb: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 1.5 }}>Configured Admins ({configuredAdmins.length})</Typography>
                        {configuredAdmins.length === 0 ? (
                            <Typography sx={{ fontSize: '0.75rem', color: '#9e9e9e' }}>No admins configured</Typography>
                        ) : (
                            <Grid container spacing={1}>
                                {configuredAdmins.map(admin => (
                                    <Grid item xs={6} sm={4} md={2.4} key={admin.id}>
                                        <Card sx={{ p: 1, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>{admin.name}</Typography>
                                            <Typography sx={{ fontSize: '0.6rem', color: '#757575' }}>{admin.email}</Typography>
                                            <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                                <Chip label={`ID: ${admin.myOperatorUserId}`} size="small"
                                                    sx={{ fontSize: '0.5rem', fontWeight: 600, height: 16,
                                                        bgcolor: '#e3f2fd', color: '#1565c0' }}
                                                />
                                                <Chip label={`${adminCounts[admin.name] || 0} calls`} size="small"
                                                    sx={{ fontSize: '0.5rem', fontWeight: 600, height: 16,
                                                        bgcolor: (adminCounts[admin.name] || 0) > 0 ? '#e8f5e9' : '#f5f5f5',
                                                        color: (adminCounts[admin.name] || 0) > 0 ? '#2e7d32' : '#9e9e9e' }}
                                                />
                                            </Box>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Card>

                    <Card sx={{ p: 2 ,width:"1256px;" }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Call List</Typography>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel sx={{ fontSize: '0.75rem' }}>Filter by Admin</InputLabel>
                                <Select
                                    value={selectedAdmin}
                                    label="Filter by Admin"
                                    onChange={(e) => setSelectedAdmin(e.target.value)}
                                    sx={{ fontSize: '0.75rem' }}
                                >
                                    <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Admins ({stats.total || 0})</MenuItem>
                                    {adminList.map(name => (
                                        <MenuItem key={name} value={name} sx={{ fontSize: '0.75rem' }}>
                                            {name} ({adminCounts[name] || 0})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box sx={{ flexGrow: 1 }} />
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DesktopDatePicker
                                    label="Select Date"
                                    value={selectedDate}
                                    onChange={(newValue) => newValue && setSelectedDate(newValue)}
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { size: 'small', sx: { '& .MuiInputBase-input': { fontSize: '0.75rem', py: 1 } } } }}
                                />
                            </LocalizationProvider>
                            <Button size="small" variant="outlined" sx={{ fontSize: '0.7rem', textTransform: 'none' }} onClick={() => setSelectedDate(dayjs())}>Today</Button>
                        </Box>
                        {filteredCalls.length === 0 ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '0.75rem', color: '#9e9e9e' }}>No calls found for selected date</Typography>
                            </Box>
                        ) : (
                            <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                                <DataGrid
                                    rows={filteredCalls}
                                    columns={columns}
                                    getRowId={(row) => row._id || row.id}
                                    pageSizeOptions={[10, 25, 50]}
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    paginationMode="server"
                                    rowCount={rowCount}
                                    loading={loading}
                                    disableRowSelectionOnClick
                                    autoHeight
                                    sx={{
                                        minWidth: 1200,
                                        '& .MuiDataGrid-footerContainer': { display: 'block' },
                                        '& .MuiDataGrid-row': { '& .MuiDataGrid-cell': { py: 1 } },
                                        '& .MuiDataGrid-columnHeaders': {
                                        borderBottom: '2px solid rgba(224, 224, 224, 1)',
                                        borderTop: '2px solid rgba(224, 224, 224, 1)',
                                        fontWeight: 600, fontSize: '0.75rem', p: 0,
                                        minHeight: '0 !important', height: '2rem'
                                    },
                                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, color: '#3e3e3e' },
                                }}
                            />
                            </Box>
                        )}
                    </Card>
                </>
            ) : (
                <Typography sx={{ color: '#d32f2f' }}>Failed to load stats</Typography>
            )}
        </Box>
    );
};

export default MyOperatorDashboard;
